import {Button, Card, Col, Divider, Form, Input, message, Modal, Row, Space, Table, Tag} from "antd";
import {CloseOutlined, DownloadOutlined, EyeOutlined, SearchOutlined,} from "@ant-design/icons";
import React, {useEffect, useRef, useState} from "react";
import dayjs from "dayjs";
import {
    DOC_CODE_OTHER_REQUEST,
    IDENTIFICATION_SOURCE,
    NOT_AVAILABLE,
    PREVIEW_DOC_TYPES,
    YYYY_MM_DD_HH_MM
} from "../../../util/Constants";
import {ComplaintRequestDto} from "../../../dto/ComplaintRequestDto";
import {FilterConfirmProps} from "antd/es/table/interface";
import Highlighter from "react-highlight-words";
import {InputRef} from "antd/es/input";
import {OtherRequestDto} from "../../../dto/OtherRequestDto";
import customerChangeRequestAdminService from "../../../service/customerChangeRequestAdminService";
import {MSG_COMPLAINTS_LIST_FETCH_FAILURE_ERROR, MSG_FAILED_TO_FETCH_DOCUMENTS_ERROR} from "../../../util/Messages";
import ErrorHandler from "../../../util/ErrorHandler";
import documentService from "../../../service/documentService";
import {UploadedDocumentDetailDto} from "../../../dto/UploadedDocumentDetailDto";
import PreviewModalComp from "../../common/preview/PreviewModalComp";
import Draggable, {DraggableData, DraggableEvent} from "react-draggable";

const {TextArea} = Input;

type DataIndex = keyof OtherRequestDto;

const OtherRequestListComp = () => {
    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const searchInput = useRef<InputRef>();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isLoading, setLoading] = useState(false);
    const [otherRequestList, setOtherRequestList] = useState<ComplaintRequestDto[]>()
    const [form] = Form.useForm();
    const [otherRequestDocument, setOtherRequestDocument] = useState<UploadedDocumentDetailDto>();
    const [docURL, setDocURL] = useState('');
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewTitle, setPreviewTitle] = useState('');
    const [docType, setDocType] = useState<PREVIEW_DOC_TYPES>(PREVIEW_DOC_TYPES.IMAGE);
    const [position2, setPosition2] = useState({ x: 100, y: 20 });
    const modalRef2 = useRef<HTMLDivElement | null>(null);
    const draggingRef2 = useRef<{ offsetX: number; offsetY: number } | null>(null);

    const onMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
        if ((e.target as HTMLElement).classList.contains('draggable-modal-title-area')) {
            if (modalRef2.current) {
                const rect = modalRef2.current.getBoundingClientRect();
                draggingRef2.current = { offsetX: e.clientX - rect.left, offsetY: e.clientY - rect.top };
            }
        }
    };

    // Drag the modal
    const onMouseMove = (e: MouseEvent) => {
        if (draggingRef2.current) {
            const { offsetX, offsetY } = draggingRef2.current;
            setPosition2({ x: e.clientX - offsetX, y: e.clientY - offsetY });
        }
    };

    // Stop dragging
    const onMouseUp = () => {
        draggingRef2.current = null;
    };

    useEffect(() => {
        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
        return () => {
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
        };
    }, []);
    const handleSearch = (
        selectedKeys: string[],
        confirm: (param?: FilterConfirmProps) => void,
        dataIndex: DataIndex,
    ) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };
    const handleReset = (clearFilters: () => void, dataIndex: DataIndex) => {
        clearFilters();
        setSearchText('');
        setSearchedColumn(dataIndex);
    };
    const getColumnSearchProps = (dataIndex: DataIndex, columnName: string): {
        filterDropdown: ({
                             setSelectedKeys,
                             selectedKeys,
                             confirm,
                             clearFilters,
                             close
                         }: {
            setSelectedKeys: any;
            selectedKeys: any;
            confirm: any;
            clearFilters: any;
            close: any
        }) => JSX.Element;
        filterIcon: (filtered: boolean) => JSX.Element;
        onFilter: (value: any, record: any) => boolean;
        render: (text: any) => any;
        onFilterDropdownOpenChange: (visible: any) => void
    } => ({
        filterDropdown: ({setSelectedKeys, selectedKeys, confirm, clearFilters, close}) => (
            <div className='table-column-search' onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${columnName}`}
                    value={selectedKeys[0]}
                    onChange={(e: {
                        target: { value: React.Key; };
                    }) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{marginBottom: 12, display: 'block'}}
                />
                <div className='search-btn-row'>
                    <Button className='tbl-search-btn'
                            type="primary"
                            onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                            icon={<SearchOutlined/>}
                            size="small"
                    >
                        Search
                    </Button>
                    <Button className='tbl-search-reset'
                            onClick={() => clearFilters && handleReset(clearFilters, dataIndex)}
                            size="small"
                    >
                        Reset
                    </Button>
                    <Button className='tbl-search-close'
                            size="small"
                            onClick={() => {
                                close();
                            }}
                    >
                        Close
                    </Button>
                </div>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{color: filtered ? '#1677ff' : undefined}}/>
        ),
        onFilter: (value, record) =>
            record[dataIndex]
                .toString()
                .toLowerCase()
                .includes((value as string).toLowerCase()),
        onFilterDropdownOpenChange: (visible) => {
            if (visible) {
                setTimeout(() => searchInput.current?.select(), 100);
            }
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{backgroundColor: '#ffc069', padding: 0}}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ''}
                />
            ) : (
                text
            ),
    });
    const columns = [
        {
            title: 'Request ID',
            dataIndex: 'id',
            key: 'id',
        },
        {
            title: 'CIN',
            dataIndex: 'cin',
            key: 'cin',
            sorter: (a: any, b: any) => {
                a = a.cin == null ? '' : a.cin
                b = b.cin == null ? '' : b.cin
                return a.localeCompare(b);
            },
            ...getColumnSearchProps('cin', 'CIN'),
        },
        {
            title: 'Customer Name',
            dataIndex: 'name',
            key: 'name',
            sorter: (a: any, b: any) => {
                a = a.name == null ? '' : a.name
                b = b.name == null ? '' : b.name
                return a.localeCompare(b);
            },
            ...getColumnSearchProps('name', 'Customer Name'),
        },
        {
            title: 'Identification Type',
            dataIndex: 'identificationType',
            key: 'identificationType',
            align: 'center',
            sorter: (a: any, b: any) => {
                a = a.identificationType == null ? '' : a.identificationType
                b = b.identificationType == null ? '' : b.identificationType
                return a.localeCompare(b);
            },
            ...getColumnSearchProps('identificationType', 'Identification Type'),
            render: (text: any, rec: any) => {
                return (
                    <Tag
                        className={getClassName(text)}
                        color={getColor(text)} key={text}>
                        {null != text ? text.toUpperCase() : 'N/A'}
                    </Tag>
                )
            }
        },
        {
            title: 'Identification Value',
            dataIndex: 'identificationValue',
            key: 'identificationValue',
            sorter: (a: any, b: any) => {
                a = a.identificationValue == null ? '' : a.identificationValue
                b = b.identificationValue == null ? '' : b.identificationValue
                return a.localeCompare(b);
            },
            ...getColumnSearchProps('identificationValue', 'Identification Value'),
        },
        {
            title: 'Request Date',
            dataIndex: 'createdDate',
            key: 'createdDate',
            sorter: (a: any, b: any) => {
                a = a.createdDate == null ? '' : a.createdDate
                b = b.createdDate == null ? '' : b.createdDate
                return a.localeCompare(b);
            },
            render: (text: any) => (
                <span>{null != text ? dayjs(text).format(YYYY_MM_DD_HH_MM) : NOT_AVAILABLE}</span>
            )
        },
        {
            title: 'Action',
            key: 'action',
            align: "center",
            fixed: 'right' as const,
            render: (rec: OtherRequestDto) => (
                <Space size="middle">
                    <Button className='btn-action-outline-common btn-action-view '
                            onClick={() => showModal(rec)}
                            icon={<EyeOutlined/>}>View</Button>
                </Space>
            ),
        },
    ];

    const getClassName = (text: string) => {
        if (null != text){
            return text.toUpperCase() == "DRIVING LICENSE" ? 'drl-tbl-tag' : 'tbl-tag'
        }
        return  'tbl-tag';
    }


    const getColor = (identificationSourceType: string) => {
        switch (identificationSourceType) {
            case IDENTIFICATION_SOURCE.NIC: {
                return 'green';
            }
            case IDENTIFICATION_SOURCE.PP: {
                return 'geekblue';
            }
            case IDENTIFICATION_SOURCE.DL: {
                return 'volcano';
            }
            default:{
                return '#8c8c8c';
            }
        }
    }

    const showModal = (record: OtherRequestDto) => {
        setPosition2({ x: 100, y: 20 });
        setIsModalOpen(true);
        setOtherRequestDocument(undefined)
        if (null != record.uploadDocumentDetailId) {
            documentService.getUploadDocumentListByTypeAndUploadedDocId(record.jobNo, record.uploadDocumentDetailId, [DOC_CODE_OTHER_REQUEST]).then(res => {
                if (res.data.status == 'SUCCESS') {
                    setOtherRequestDocument(res.data.results[0].uploadDocumentDetailDto);
                }
            }, (reason) => {
                ErrorHandler.handleError(reason);
            });
        }
        form.setFieldsValue({otherRequest: record.otherRequest})
    };

    const handleCancel = () => {
        setIsModalOpen(false);
        setPreviewOpen(false);
        setPosition2({ x: 100, y: 20 });
    };

    const handlePreviewCancel = () => {
        setPreviewOpen(false);
        setPosition2({ x: 100, y: 20 });
    };

    useEffect(() => {
        fetchComplaintsList()
    }, []);

    const fetchComplaintsList = () => {
        setLoading(true)
        customerChangeRequestAdminService.getPendingOtherRequests().then((res) => {
            if (res.data.results) {
                setOtherRequestList(res.data.results)
                setLoading(false)
            } else {
                message.error(MSG_COMPLAINTS_LIST_FETCH_FAILURE_ERROR)
                setLoading(false)
            }
        }).catch(reason => {
            ErrorHandler.handleError(reason)
            setLoading(false)
        })
    }
    const openModal = (document: UploadedDocumentDetailDto) => {
        documentService.viewOriginalDocument(document.uploadDocumentDetailId.toString()).then(res => {
            // setCapturedDocumentCompSpinning(false)
            if (res.data !== null) {
                setPosition2({ x: 100, y: 20 });
                setPreviewOpen(true)
                setIsModalOpen(true)
                setPreviewTitle(document.originalDocumentName);
                setDocURL(res.data.results)
                setDocType(checkDocumentContentType(res.data.results))
            } else {
                message.error(MSG_FAILED_TO_FETCH_DOCUMENTS_ERROR).then();
            }
        }, (reason) => {
            // setCapturedDocumentCompSpinning(false)
            ErrorHandler.handleError(reason);
        })
        return true;
    };
    const checkDocumentContentType = (base64: string): PREVIEW_DOC_TYPES => {

        if (base64.includes("image/png") || base64.includes("image/jpeg") || base64.includes("image/jpg")) {
            return PREVIEW_DOC_TYPES.IMAGE;
        } else {
            return PREVIEW_DOC_TYPES.PDF;
        }

    };
    const download = (val: string, docName: string) => {
        console.log("docName", docName)
        let a = document.createElement('a');
        a.href = val;
        a.download = docName;
        a.click();
    }


    return (
        <>
            <Row gutter={[24, 12]}>
                <Col span={24}>
                    <Table size='small' loading={isLoading} dataSource={otherRequestList} columns={columns}
                           scroll={{x: 'max-content'}}></Table>
                </Col>
            </Row>
            <Modal title={'Other Request'}
                   style={{top: 0, right: 0, margin: 20,marginTop: 20, position: 'absolute'}}
                   maskStyle={{top: 0, left: 0}}
                   open={isModalOpen} footer={null} onCancel={handleCancel} maskClosable={false}
                   className={'modal-body-scroll-only disabled-data-show-inputs-container'}
                   width={'50vw'}
            >
                <Row gutter={[24, 12]} className={'form-item-mb-12 modal-body-scroll-only-main-row compare-modal-height'} >
                    <Col span={24}>
                        <Card title="Request Details" size={'small'}>
                            <Form
                                className='form-input'
                                layout={"vertical"}
                                form={form}
                            >
                                <Row gutter={[12, 0]}>
                                    <Col span={24}>
                                        <Form.Item
                                            // label="Request"
                                            name={"otherRequest"}>
                                            <TextArea rows={4} disabled/>
                                        </Form.Item>
                                    </Col>
                                    {otherRequestDocument ? <Col span={24} style={{marginBottom: 16, paddingRight: 28}}>
                                        <div className='utility_bill img-nic'>
                                            <div className='utulity_bill_icon_div'>
                                                <div className="img_div">
                                                    <img src={otherRequestDocument?.thumbB64} alt=""/>
                                                </div>
                                                <div className='name_div'>
                                                    <h5>{otherRequestDocument?.documentTypeName}</h5>
                                                    {/*<h6>Size: 5MB </h6>*/}
                                                </div>
                                            </div>
                                            <Button className='doc-view-btn'
                                                    onClick={() => openModal(otherRequestDocument)}
                                            ><EyeOutlined/></Button>
                                        </div>
                                    </Col> : <></>}

                                </Row>
                            </Form>
                        </Card>
                    </Col>

                    <Col span={24} className={'draggable-footer-cancel-col'}>
                        <Divider style={{marginBottom: 12}}></Divider>
                        <Space style={{float: 'right'}}>
                            <Button
                                    onClick={handleCancel}
                                    style={{
                                        width: 80,
                                    }}>Cancel</Button>
                        </Space>
                    </Col>
                </Row>

            </Modal>

            {/*<Modal*/}
            {/*    className={docType == PREVIEW_DOC_TYPES.PDF ? "modal-custom-bg" : "image-view-modal"}*/}
            {/*    open={previewOpen}*/}
            {/*    centered*/}
            {/*    title={previewTitle}*/}
            {/*    onCancel={handlePreviewCancel}*/}
            {/*    destroyOnClose={true}*/}
            {/*    width={docType == PREVIEW_DOC_TYPES.PDF ? 800 : 600}*/}
            {/*    footer={[*/}
            {/*        <Button type="primary" icon={<DownloadOutlined/>} style={{background: '#FF7400', height: 40}}*/}
            {/*                htmlType="submit" onClick={() => download(docURL, previewTitle)}>*/}
            {/*            Download*/}
            {/*        </Button>]}>*/}
            {/*    <PreviewModalComp docURL={docURL}/>*/}
            {/*</Modal>*/}
            {previewOpen &&
                <div
                    ref={modalRef2}
                    className="draggable-modal"
                    style={{
                        position: 'fixed',
                        top: position2.y,
                        left: position2.x,
                        zIndex: 100000,
                        width: '40vw'
                    }}
                    onMouseDown={onMouseDown}
                >
                    <div className="draggable-modal-title-area">
                        <span className={'modal-title'}>{previewTitle}</span>
                        <Button icon={<CloseOutlined/>} size={'small'} onClick={handlePreviewCancel}/>
                    </div>
                    <div className="modal-body-area compare-preview-modal-height" >
                        <PreviewModalComp docURL={docURL}/>
                    </div>
                    <div className="modal-footer-area">
                        <Button type="primary" icon={<DownloadOutlined/>} style={{background: '#FF7400', height: 40}}
                                htmlType="submit" onClick={() => download(docURL, previewTitle)}>
                            Download
                        </Button>
                    </div>
                </div>
            }
        </>
    )
}
export default OtherRequestListComp;